import { motion } from "framer-motion";
import { PawPrint } from "lucide-react";

const accents = ["#4FA3C6", "#1F7A8C", "#F59E0B", "#FF8C6B", "#6BAFCC", "#6ED3C3"];

const team = [
  { name: "Helen", role: "Chief Tiddler · Brand Development Director", bio: "Our founder. Helen started with just three tiddlers in a school pool — her passion built everything you see today. She leads our teaching team and shapes our whole approach.", photo: `${process.env.PUBLIC_URL}/team/helen.jpg` },
  { name: "Nicky", role: "Experience Director", bio: "The warm welcome behind the scenes. Nicky looks after everything out of the pool and can always be found chatting with families — especially the children.", photo: `${process.env.PUBLIC_URL}/team/nicky.jpg` },
  { name: "Jo", role: "Leadership Team · Swim Teacher", bio: "A Tiddler family first, Jo took the plunge to join us in 2018. Now part of our leadership team, she brings knowledge, heart and a fresh perspective.", photo: `${process.env.PUBLIC_URL}/team/jo.jpg` },
  { name: "Sally", role: "Leadership Team · Swim Coordinator", bio: "Sally leads our team of swim teachers and aquatic helpers, keeping every lesson running beautifully. She brings a wealth of experience and a wonderful sense of humour to poolside.", photo: `${process.env.PUBLIC_URL}/team/sally.jpg` },
  { name: "Bex", role: "Leadership Team · Office & Happiness Team Lead", bio: "Bex oversees our office and leads our team of Happiness Heroes, making sure every family gets a warm welcome. Smiley and happy, she loves chatting with the children about their week.", photo: `${process.env.PUBLIC_URL}/team/bex.jpg` },
  { name: "Emma", role: "Swim Teacher", bio: "Bright, energetic and fun. Emma's love of the water shines through as she helps families learn to love the water and grow in confidence.", photo: `${process.env.PUBLIC_URL}/team/emma.jpg` },
  { name: "Mollie", role: "Swim Teacher", bio: "A swimmer herself, Mollie is a natural teacher who shines in the pool and knows exactly how to help little ones find their confidence.", photo: `${process.env.PUBLIC_URL}/team/mollie.jpg` },
  { name: "Scarlett", role: "Swim Teacher", bio: "A ray of sunshine on a Saturday afternoon. Scarlett loves her job and her passion shows in every single lesson.", photo: `${process.env.PUBLIC_URL}/team/scarlett.jpg` },
  { name: "Harry", role: "Aquatic Helper", bio: "Poolside sidekick and the extra pair of hands (plus endless high-fives) that keep every lesson flowing.", photo: `${process.env.PUBLIC_URL}/team/harry.jpg` },
  { name: "Archie", role: "Aquatic Helper", bio: "Big energy, bigger smiles. Archie has a knack for making every splash feel like playtime.", photo: `${process.env.PUBLIC_URL}/team/archie.jpg` },
  { name: "Maya", role: "Aquatic Helper", bio: "Gentle and endlessly patient, Maya is brilliant at helping nervous first-timers find their confidence.", photo: `${process.env.PUBLIC_URL}/team/maya.jpg` },
  { name: "Laura", role: "Happiness Hero · Master of Coin", bio: "Smiley and engaging, Laura supports our families in person and by email — and quietly keeps our books in perfect order.", photo: `${process.env.PUBLIC_URL}/team/laura.jpg` },
  { name: "Michelle", role: "Happiness Hero", bio: "Our resident problem solver. With a wealth of experience with children and adults alike, Michelle feels right at home at Happy Tiddlers.", photo: `${process.env.PUBLIC_URL}/team/michelle.jpg` },
  { name: "Mae", role: "Happiness Hero", bio: "Warm welcomes are Mae's specialty — she makes sure every family feels right at home from the moment they walk in.", photo: `${process.env.PUBLIC_URL}/team/mae.jpg` },
  { name: "Chloe", role: "Happiness Hero", bio: "Bright and bubbly, Chloe is always on hand with a smile, a wave and a solution to just about anything.", photo: `${process.env.PUBLIC_URL}/team/chloe.jpg` },
  { name: "Verity", role: "Marketing Guru", bio: "A customer first, Verity looks after our social media and is always focused on how we can do even better. Never without a smile.", photo: `${process.env.PUBLIC_URL}/team/verity.jpg` },
  { name: "Vici", role: "Sparkle Sorcerer · Facilities", bio: "Vici works her magic behind the scenes, keeping our centre gleaming from poolside to changing rooms. That spotless sparkle? All her.", photo: `${process.env.PUBLIC_URL}/team/vici.jpg` },
];

const dogs = [
  { name: "Mr Humbug", role: "Top Dog", note: "Here since our pool centre opened in 2018 — and gets more attention than the whole office team!", photo: `${process.env.PUBLIC_URL}/team-humbug.jpg` },
  { name: "Miss Bojingles", role: "Foot-Warming Specialist", note: "A loveable pooch who adores the attention of the tiddlers (and warming cold feet).", photo: `${process.env.PUBLIC_URL}/team-jingles.jpg` },
];

const initials = (name) =>
  name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

export const Team = ({ hideHeader = false }) => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="team-section">
      <div className="container mx-auto px-6 md:px-12">
        {!hideHeader && (
          <div className="max-w-2xl mb-14">
            <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Meet Team Tiddler</span>
            <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
              The friendly faces behind every splash
            </h2>
            <p className="mt-5 text-lg text-[#334155]">
              Warm, qualified and genuinely passionate — our team is the heart of Happy Tiddlers.
              They're the reason nervous first-timers become confident swimmers, and why families
              keep coming back year after year.
            </p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {team.map((m, i) => {
            const accent = accents[i % accents.length];
            return (
              <motion.div
                key={`${m.name}-${i}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="group bg-[#F8FAFC] rounded-[1.75rem] p-7 border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-500"
                data-testid={`team-member-${i}`}
              >
                <div className="flex items-center gap-4">
                  {m.photo ? (
                    <span className="w-16 h-16 rounded-2xl overflow-hidden shrink-0 group-hover:scale-105 transition-transform duration-300">
                      <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />
                    </span>
                  ) : (
                    <span
                      className="grid place-items-center w-16 h-16 rounded-2xl text-white font-heading font-black text-xl shrink-0 group-hover:scale-105 transition-transform duration-300"
                      style={{ backgroundColor: accent }}
                    >
                      {initials(m.name)}
                    </span>
                  )}
                  <div>
                    <h3 className="font-heading text-lg font-bold text-[#0F172A] leading-tight">{m.name}</h3>
                    <p className="mt-0.5 text-sm font-semibold" style={{ color: accent }}>{m.role}</p>
                  </div>
                </div>
                <p className="mt-4 text-[#64748B] leading-relaxed">{m.bio}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Pool dogs */}
        <div className="mt-10 grid sm:grid-cols-2 gap-6 md:gap-8">
          {dogs.map((d, i) => (
            <motion.div
              key={d.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="flex items-center gap-4 rounded-[1.75rem] bg-[#0A2540] p-6 text-white"
              data-testid={`team-dog-${i}`}
            >
              <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#FF7A1A] text-white shrink-0 overflow-hidden">
                {d.photo ? (
                  <img src={d.photo} alt={d.name} className="w-full h-full object-cover" />
                ) : (
                  <PawPrint size={24} />
                )}
              </span>
              <div>
                <h3 className="font-heading text-lg font-bold leading-tight">
                  {d.name} <span className="text-sm font-semibold text-[#5EEAD4]">· {d.role}</span>
                </h3>
                <p className="mt-1 text-sm text-[#CBD5E1] leading-relaxed">{d.note}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
;
